import type { EventDraft, GuestRsvpPageState } from './rsvpDraftState';
import {
  createEventDrafts,
  getGuestRsvpSubmissionStatus,
  getParticipantEventDraftKey,
  getRsvpDraftStorageKey,
  isDeadlinePassed,
  normalizeRemoteEventDrafts,
} from './rsvpDraftState';

export function buildInitialDrafts(input: {
  events: GuestRsvpPageState['events'];
  identityContext: GuestRsvpPageState['identityContext'];
  occasionSlug: string;
  guestToken: string;
  remoteDraft: unknown;
  storedDrafts: string | null;
}) {
  const nextDrafts = createEventDrafts(input.events, input.identityContext);
  const remoteDrafts = normalizeRemoteEventDrafts(input.remoteDraft);

  for (const eventState of input.events) {
    for (const participant of getEventDraftParticipants(
      eventState,
      input.identityContext,
    )) {
      const draftKey = getParticipantEventDraftKey(
        eventState.eventId,
        participant.participantActorId,
      );
      const remoteDraft =
        remoteDrafts[draftKey] ?? remoteDrafts[eventState.eventId];
      if (!remoteDraft || isDeadlinePassed(eventState.deadlineAt)) {
        continue;
      }
      nextDrafts[draftKey] = {
        ...nextDrafts[draftKey],
        ...remoteDraft,
        linkedFormAnswers: {
          ...nextDrafts[draftKey]?.linkedFormAnswers,
          ...remoteDraft.linkedFormAnswers,
        },
      };
    }
  }

  if (!input.storedDrafts) {
    return nextDrafts;
  }

  try {
    const parsedDrafts = JSON.parse(input.storedDrafts) as Record<
      string,
      EventDraft
    >;

    for (const eventState of input.events) {
      for (const participant of getEventDraftParticipants(
        eventState,
        input.identityContext,
      )) {
        const draftKey = getParticipantEventDraftKey(
          eventState.eventId,
          participant.participantActorId,
        );
        const storedDraft =
          parsedDrafts[draftKey] ?? parsedDrafts[eventState.eventId];
        if (!storedDraft || isDeadlinePassed(eventState.deadlineAt)) {
          continue;
        }
        nextDrafts[draftKey] = {
          ...nextDrafts[draftKey],
          ...storedDraft,
          linkedFormAnswers: {
            ...nextDrafts[draftKey]?.linkedFormAnswers,
            ...storedDraft.linkedFormAnswers,
          },
        };
      }
    }
  } catch {
    window.localStorage.removeItem(
      getRsvpDraftStorageKey(input.occasionSlug, input.guestToken),
    );
  }

  return nextDrafts;
}

export function getPersistedDrafts(input: {
  events: GuestRsvpPageState['events'];
  identityContext: GuestRsvpPageState['identityContext'];
  drafts: Record<string, EventDraft>;
}) {
  const editableDraftKeys = new Set(
    input.events.flatMap((event) =>
      isDeadlinePassed(event.deadlineAt)
        ? []
        : getEventDraftParticipants(event, input.identityContext).map(
            (participant) =>
              getParticipantEventDraftKey(
                event.eventId,
                participant.participantActorId,
              ),
          ),
    ),
  );

  return Object.fromEntries(
    Object.entries(input.drafts).filter(([draftKey]) =>
      editableDraftKeys.has(draftKey),
    ),
  );
}

export function getDraftSyncSignature(drafts: Record<string, EventDraft>) {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(drafts)
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
        .map(([draftKey, draft]) => [
          draftKey,
          {
            ...draft,
            linkedFormAnswers: Object.fromEntries(
              Object.entries(draft.linkedFormAnswers).sort(
                ([leftKey], [rightKey]) => leftKey.localeCompare(rightKey),
              ),
            ),
          },
        ]),
    ),
  );
}

export function persistDraftsToStorage(input: {
  occasionSlug: string;
  guestToken: string;
  drafts: Record<string, EventDraft>;
}) {
  const storageKey = getRsvpDraftStorageKey(
    input.occasionSlug,
    input.guestToken,
  );

  if (Object.keys(input.drafts).length === 0) {
    window.localStorage.removeItem(storageKey);
    return;
  }

  window.localStorage.setItem(storageKey, JSON.stringify(input.drafts));
}

export function buildRsvpSubmissionEvents(input: {
  events: GuestRsvpPageState['events'];
  identityContext: GuestRsvpPageState['identityContext'];
  drafts: Record<string, EventDraft>;
  householdNote: string;
}) {
  const trimmedHouseholdNote = input.householdNote.trim();

  return input.events.flatMap((eventState) => {
    if (isDeadlinePassed(eventState.deadlineAt)) {
      return [];
    }

    return getEventDraftParticipants(eventState, input.identityContext).map(
      (participant) => {
        const draftKey = getParticipantEventDraftKey(
          eventState.eventId,
          participant.participantActorId,
        );
        const draft = input.drafts[draftKey];
        const trimmedDraftNote = draft?.note.trim() ?? '';

        return {
          eventId: eventState.eventId,
          participantActorId: participant.participantActorId,
          status: getGuestRsvpSubmissionStatus(draft),
          note:
            trimmedDraftNote.length > 0
              ? trimmedDraftNote
              : trimmedHouseholdNote.length > 0
                ? trimmedHouseholdNote
                : null,
          linkedFormAnswers: eventState.linkedForm
            ? eventState.linkedForm.fields.map((field) => ({
                fieldId: field.id,
                value: draft?.linkedFormAnswers[field.id] ?? null,
              }))
            : undefined,
        };
      },
    );
  });
}

export function getLatestResponseTimestamp(pageState: GuestRsvpPageState) {
  return (
    pageState.events
      .map((event) => event.response?.updatedAt ?? null)
      .concat(
        pageState.events.flatMap((event) =>
          event.participantResponses.map(
            (participant) => participant.response?.updatedAt ?? null,
          ),
        ),
      )
      .filter((value): value is string => value !== null)
      .sort((left, right) => Date.parse(right) - Date.parse(left))[0] ?? null
  );
}

function getEventDraftParticipants(
  eventState: GuestRsvpPageState['events'][number],
  identityContext: GuestRsvpPageState['identityContext'],
) {
  if (eventState.participantResponses.length > 0) {
    return eventState.participantResponses;
  }

  return identityContext.participantActorIds.map(
    (participantActorId, index) => ({
      participantActorId,
      participantDisplayName:
        identityContext.participantDisplayNames[index] ?? participantActorId,
      response:
        eventState.response?.participantActorIds.includes(
          participantActorId,
        ) === true
          ? eventState.response
          : null,
    }),
  );
}
